import React, { createContext, useContext, useMemo } from 'react';
import type { ViewProps } from 'react-native';

import { useCustomer } from '@order/Customer';
import { useCart, useCustomerCredit } from '@order/hooks';

import {
  useAvailableWantedTimes,
  useCheckoutCredit,
  useCheckoutFieldsValidation,
  useCheckoutHandlers,
  useCheckoutLedger,
  useCheckoutPayload,
  useDeliveryEstimate,
  useFutureDateModal,
  useLocationTippingEnabled,
  useOrderAddress,
  useOrderValidation,
  useSubmitOrder,
} from './hooks';

// ─── Context ──────────────────────────────────────────────────────

const CheckoutContext = createContext<CheckoutCtx | undefined>(undefined);

export const CheckoutContextProvider = ({ children }: ViewProps) => {
  const payloadState = useCheckoutPayload();

  // ─── Checkout Queries / Mutations ───────────────────────────────

  const customerData = useCustomer();
  const cartData = useCart();
  const wantedTimesData = useAvailableWantedTimes();
  const creditData = useCustomerCredit();

  // ─── Query Derived Data ─────────────────────────────────────────

  const deliveryAddressId = cartData.cart?.deliveryOrderDetail?.address?.id;
  const deliveryEstimateData = useDeliveryEstimate(deliveryAddressId);
  const addressData = useOrderAddress(cartData.cart);

  // ─── Checkout State ─────────────────────────────────────────────

  const futureDateModalState = useFutureDateModal();
  const validationState = useCheckoutFieldsValidation(cartData.cartOrderType);
  const payloadHandlers = useCheckoutHandlers({
    setPayload: payloadState.setPayload,
    setShouldShowFutureDateModal:
      futureDateModalState.setShouldShowFutureDateModal,
  });

  // ─── Tipping ─────────────────────────────────────────────────────────

  const isTippingEnabledForLocation = useLocationTippingEnabled();

  // ─── State Derived Data ─────────────────────────────────────────

  const checkoutCreditData = useCheckoutCredit({
    ...cartData,
    ...creditData,
    ...payloadState,
  });

  const orderValidation = useOrderValidation({
    ...cartData,
    ...payloadState.payload,
  });

  const ledgerData = useCheckoutLedger({
    cart: cartData.cart,
    payload: payloadState.payload,
    credit: checkoutCreditData.checkoutCredit,
  });

  // ────────────────────────────────────────────────────────────────

  const submitOrderData = useSubmitOrder({
    cartData,
    payload: payloadState.payload,
    ledger: ledgerData.ledger,
  });

  // ─── Memoized Data ──────────────────────────────────────────────

  const value: CheckoutCtx = useMemo(
    () => ({
      ...customerData,
      ...cartData,
      ...wantedTimesData,
      ...creditData,
      ...submitOrderData,
      ...deliveryEstimateData,
      ...addressData,
      ...payloadState,
      ...futureDateModalState,
      ...validationState,
      ...payloadHandlers,
      ...checkoutCreditData,
      ...orderValidation,
      ...ledgerData,
      isTippingEnabledForLocation,
    }),
    [
      customerData,
      cartData,
      wantedTimesData,
      creditData,
      submitOrderData,
      deliveryEstimateData,
      addressData,
      payloadState,
      futureDateModalState,
      validationState,
      payloadHandlers,
      checkoutCreditData,
      orderValidation,
      ledgerData,
      isTippingEnabledForLocation,
    ],
  );

  // ─── Context Provider ───────────────────────────────────────────

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

// ─── Hooks ────────────────────────────────────────────────────────

export const useCheckoutContext = () => {
  const context = useContext(CheckoutContext);

  if (context === undefined) {
    throw new Error(
      'useCheckoutContext must be used within a <CheckoutContextProvider>',
    );
  }

  return context;
};

// ─── Types ────────────────────────────────────────────────────────

type CheckoutCtx = ReturnType<typeof useCheckoutPayload> &
  ReturnType<typeof useFutureDateModal> &
  ReturnType<typeof useCheckoutFieldsValidation> &
  ReturnType<typeof useCheckoutHandlers> &
  ReturnType<typeof useCustomer> &
  ReturnType<typeof useCart> &
  ReturnType<typeof useDeliveryEstimate> &
  ReturnType<typeof useOrderAddress> &
  ReturnType<typeof useAvailableWantedTimes> &
  ReturnType<typeof useCustomerCredit> &
  ReturnType<typeof useSubmitOrder> &
  ReturnType<typeof useCheckoutCredit> &
  ReturnType<typeof useOrderValidation> &
  ReturnType<typeof useCheckoutLedger> & {
    readonly isTippingEnabledForLocation: boolean;
  };
